
import { z } from "zod";

// Define schema for the reverse auction form
export const reverseAuctionFormSchema = z.object({
  enableReverseAuction: z.enum(["yes", "no"], {
    required_error: "Please select whether to enable reverse auction",
  }),
  timezone: z.string().default("GMT+0"),
  auctionDuration: z.string().optional(),
  customWeeks: z.coerce.number().min(0).default(0),
  customDays: z.coerce.number().min(0).default(0),
  customHours: z.coerce.number().min(0).default(0),
  proposalDueDate: z.date().optional(),
  proposalDueTime: z.string().optional(),
  auctionStartDate: z.date().optional(),
  auctionStartTime: z.string().optional(),
  includePricing: z.boolean().default(false),
  notifyBidders: z.boolean().default(true),
  bidderVisibility: z.string().default("price-ranking"),
  additionalNotes: z.string().optional(),
}).refine(
  (data) => {
    // Skip validation if reverse auction is disabled or if either date is missing
    if (data.enableReverseAuction === "no" || !data.proposalDueDate || !data.auctionStartDate) {
      return true;
    }
    
    // Create date objects with the time components if available
    const proposalDate = new Date(data.proposalDueDate);
    const auctionDate = new Date(data.auctionStartDate);
    
    // Set time if available
    if (data.proposalDueTime) {
      const [hours, minutes] = data.proposalDueTime.split(':').map(Number);
      proposalDate.setHours(hours, minutes);
    }
    
    if (data.auctionStartTime) {
      const [hours, minutes] = data.auctionStartTime.split(':').map(Number);
      auctionDate.setHours(hours, minutes);
    }
    
    // Auction date must be after proposal due date
    return auctionDate > proposalDate;
  },
  {
    message: "Auction start date must be after the proposal due date",
    path: ["auctionStartDate"], // Show error on the auction start date field
  }
);

// Define type for the form values
export type ReverseAuctionFormValues = z.infer<typeof reverseAuctionFormSchema>;

// Default values for the reverse auction form
export const reverseAuctionDefaultValues: Partial<ReverseAuctionFormValues> = {
  enableReverseAuction: undefined,
  timezone: "GMT+0",
  auctionDuration: "",
  customWeeks: 0,
  customDays: 0,
  customHours: 0,
  proposalDueDate: undefined,
  proposalDueTime: "",
  auctionStartDate: undefined,
  auctionStartTime: "",
  includePricing: false,
  notifyBidders: true,
  bidderVisibility: "price-ranking",
  additionalNotes: "",
};

// Duration options for the auction
export const auctionDurationOptions = [
  { value: "30min", label: "30 Minutes" },
  { value: "1hour", label: "1 Hour" },
  { value: "2hours", label: "2 Hours" },
  { value: "4hours", label: "4 Hours" },
  { value: "1day", label: "1 Day" },
  { value: "2days", label: "2 Days" },
  { value: "1week", label: "1 Week" },
  { value: "custom", label: "Custom" },
];

// Timezone options
export const timezoneOptions = [
  { value: "GMT-12", label: "GMT-12" },
  { value: "GMT-11", label: "GMT-11" },
  { value: "GMT-10", label: "GMT-10" },
  { value: "GMT-9", label: "GMT-9" },
  { value: "GMT-8", label: "GMT-8" },
  { value: "GMT-7", label: "GMT-7" },
  { value: "GMT-6", label: "GMT-6" },
  { value: "GMT-5", label: "GMT-5" },
  { value: "GMT-4", label: "GMT-4" },
  { value: "GMT-3", label: "GMT-3" },
  { value: "GMT-2", label: "GMT-2" },
  { value: "GMT-1", label: "GMT-1" },
  { value: "GMT+0", label: "GMT+0" },
  { value: "GMT+1", label: "GMT+1" },
  { value: "GMT+2", label: "GMT+2" },
  { value: "GMT+3", label: "GMT+3" },
  { value: "GMT+4", label: "GMT+4" },
  { value: "GMT+5", label: "GMT+5" },
  { value: "GMT+6", label: "GMT+6" },
  { value: "GMT+7", label: "GMT+7" },
  { value: "GMT+8", label: "GMT+8" },
  { value: "GMT+9", label: "GMT+9" },
  { value: "GMT+10", label: "GMT+10" },
  { value: "GMT+11", label: "GMT+11" },
  { value: "GMT+12", label: "GMT+12" },
];

// Bidder visibility options
export const bidderVisibilityOptions = [
  { value: "price-ranking", label: "Price Ranking" },
];
